import deepEqual from "fast-deep-equal";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import { zslAtom } from "../templates";
import { ZSLInterface } from "../types";
import { extractAssetId, isTemplateInterfaceProjectId } from "../utils";
import { aiSuggestedEditsAtomFamily } from "../nodes";
import { fetchInterfaceProjectAtomFamily_SUSPENDS } from "./fetchInterfaceProjectAtomFamily_SUSPENDS";

export const interfaceProjectAtomFamily_SUSPENDS = atomFamily(
  ({
    interfaceProjectId,
    proposalId,
    nodeId,
  }: {
    interfaceProjectId: string | undefined;
    proposalId?: string | undefined;
    nodeId: string;
  }) => {
    const baseAtom = atom(async (get) => {
      if (!interfaceProjectId && !proposalId) {
        return;
      }
      // if proposalId is provided, we are using the ai suggested edits and it is a template interface
      const isTemplateInterface =
        isTemplateInterfaceProjectId(interfaceProjectId) || proposalId;

      if (isTemplateInterface) {
        const aiProposedEdits = get(aiSuggestedEditsAtomFamily(nodeId));
        const zsl = get(zslAtom);

        if (aiProposedEdits && proposalId) {
          const aiProposed = aiProposedEdits.additions.find(
            (addition) => addition.id === proposalId,
          );

          if (aiProposed) {
            if (aiProposed.zsl?.interfaces) {
              return aiProposed.zsl.interfaces[0];
            }
            if (aiProposed?.outline?.interfaces?.[0]) {
              return aiProposed.outline.interfaces[0];
            }
          }
        }

        if (zsl && interfaceProjectId) {
          const coreInterfaceProjectId = extractAssetId(interfaceProjectId);
          const zslInterface = zsl.interfaces?.find(
            (project) => coreInterfaceProjectId === project.id,
          );
          return zslInterface as ZSLInterface;
        }

        // Because we don't want to fetch with template asset id and get a 404 Not Found error
        return;
      }

      // Delegate API retrieval to the shared fetch atom
      const result = await get(
        fetchInterfaceProjectAtomFamily_SUSPENDS(interfaceProjectId),
      );

      // Unwrap success variant; degrade all other variants to `null`
      if (result?.type === "success") {
        return result.data;
      }
      return null;
    });

    baseAtom.debugLabel = `interfaceProjectAtomFamily_SUSPENDS(${interfaceProjectId}-nodeId:${nodeId})`;
    return baseAtom;
  },
  deepEqual,
);
